import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import omit from 'lodash/omit';

import config from 'js/app/config';
import Retracked from 'js/app/retracked';
import { getAuthMode, getLocationHash, stripAuthModeFromHash } from 'js/lib/authModeDeepLinkUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import AuthenticationContent from 'bundles/authentication-common/components/authenticationModal/AuthenticationContent';
import Modal from 'bundles/authentication-common/components/modals/Modal';
import { AUTHENTICATION_MODAL_MODES, isAuthenticationModalMode } from 'bundles/authentication-common/constants';
import type { AuthModalContext } from 'bundles/authentication-common/types';
import { shouldShowProgressiveAuth } from 'bundles/authentication-common/utils';
import { getAbsoluteRedirectToURL } from 'bundles/authentication-common/utils/navigationUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AuthenticationContentBaseProps, AuthenticationModalBaseProps } from 'bundles/user-account/types';

import _t from 'i18n!nls/authentication';

export type PropsFromCaller = {
  /**
   * Use this prop to specify any additional variables, functions, etc. that a specific view in <AuthenticationContent>
   * might need
   */
  context?: AuthModalContext;
} & AuthenticationContentBaseProps &
  AuthenticationModalBaseProps;

const AuthenticationModal = (props: PropsFromCaller) => {
  const { defaultMode, isEnterprise, programId, onCloseRedirectURL, onClose, utilizeWebRedirectOnClose, ...restProps } =
    props;
  const location = useLocation();
  const navigate = useNavigate();
  const locationHash = getLocationHash(location);

  const [isOpen, setIsOpen] = useState(false);

  const { redirectTo: defaultRedirectTo, context: contextFromProps } = props;
  const { query } = location;
  const {
    authProvider,
    verificationEmailRedirectUrl,
    authType,
    redirectTo: redirectToFromQuery,
    r,
    action,
    serviceCode,
    completeMode,
  } = query;
  const modeCandidate = defaultMode || getAuthMode(location.query, locationHash);
  const mode = isAuthenticationModalMode(modeCandidate) ? modeCandidate : undefined;

  useEffect(() => {
    const tracker = Retracked.makeTracker({
      namespace: 'open_course.user', // for backwards compatibility with userAuthorization
    });

    tracker(mode === AUTHENTICATION_MODAL_MODES.LOGIN ? 'login_modal.open' : 'signup_modal.open');

    const requestAnimationFrameId = requestAnimationFrame(() => {
      setIsOpen(true);
    });

    return () => cancelAnimationFrame(requestAnimationFrameId);
  }, [defaultMode, location.query, locationHash, isEnterprise, programId, mode]);

  const handleClose = useCallback<() => void>(() => {
    setIsOpen(false);
    onClose?.();

    if (onCloseRedirectURL) {
      // I'm not using the redirect authentication action because I want the caller to be able to pass
      // in just a path and not a full url.
      if (utilizeWebRedirectOnClose) {
        window.location.replace(onCloseRedirectURL);
      } else {
        navigate({ pathname: onCloseRedirectURL });
      }
      return;
    }

    const currentPath = location.pathname;
    const query = omit(location.query, 'authMode');
    const searchParams = new URLSearchParams();

    // Add remaining query parameters
    Object.entries(query).forEach(([key, value]) => {
      if (value != null) {
        searchParams.set(key, String(value));
      }
    });

    const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const strippedHash = stripAuthModeFromHash(locationHash) || '';

    navigate({
      pathname: currentPath,
      search: newSearch,
      hash: strippedHash,
    });
  }, [
    onClose,
    onCloseRedirectURL,
    utilizeWebRedirectOnClose,
    navigate,
    location.pathname,
    location.query,
    locationHash,
  ]);

  const currentURL = new URL(`${location.pathname}${location.search}${locationHash ?? ''}`, config.url.base);
  currentURL.searchParams.delete('authMode');
  const strippedFragment = stripAuthModeFromHash(locationHash);
  if (strippedFragment) {
    currentURL.hash = strippedFragment.startsWith('#') ? strippedFragment.slice(1) : strippedFragment;
  } else {
    currentURL.hash = '';
  }
  const showProgressiveAuth = shouldShowProgressiveAuth({
    shouldDisableProgressiveAuth: !!isEnterprise || !!programId || !!authProvider,
  });

  const redirectTo = defaultRedirectTo || redirectToFromQuery || r;
  const redirectToURL = getAbsoluteRedirectToURL(redirectTo);
  const context = {
    action,
    accountType: serviceCode,
    authProvider,
    mode: completeMode,
    authMode: mode,
    service: authType,
    verificationEmailRedirectUrl,
    isEnterprise,
    showProgressiveAuth,
    ...contextFromProps,
  };

  const renderModal = () => {
    const commonProps = {
      ...restProps,
      contentLabel: _t('Authentication modal'),
      isOpen,
      onClose: handleClose,
    };
    const authContent = (
      <AuthenticationContent
        {...restProps}
        context={context}
        defaultMode={mode}
        redirectTo={redirectTo}
        ssoRedirectTo={redirectTo}
        programId={programId}
        magicLinkRedirect={redirectToURL || currentURL.toString()}
      />
    );

    return <Modal {...commonProps}>{authContent}</Modal>;
  };

  return (
    <TrackedDiv
      className="rc-AuthenticationModal"
      trackingName="authentication_modal"
      data-e2e="auth-modal"
      data-testid="auth-modal"
      trackClicks={false}
      withVisibilityTracking
    >
      {renderModal()}
    </TrackedDiv>
  );
};

export default Retracked.createTrackedContainer<PropsFromCaller>(() => ({
  namespace: {
    app: 'authentication',
  },
}))(AuthenticationModal);
